import { useEffect, useMemo, useRef, useState } from "react";
import { Card } from "./Card";
import { AlertIcon, TerminalIcon, TrashIcon } from "./Icons";
import { analyzeBootLog } from "../flasher/bootAnalysis";
import type { LogEntry } from "../hooks/useFlasher";

type Tab = "log" | "monitor";

interface Props {
  logs: LogEntry[];
  monitorText: string;
  monitorActive: boolean;
  canMonitor: boolean;
  onStartMonitor: () => void;
  onStopMonitor: () => void;
  onClearLogs: () => void;
  onClearMonitor: () => void;
}

function formatTime(timestamp: number): string {
  return new Date(timestamp).toLocaleTimeString(undefined, { hour12: false });
}

/**
 * Keeps a scroll container pinned to the bottom, but stops following as soon as
 * the user scrolls up so they can read back through a long log.
 */
function useStickyScroll(dependency: unknown) {
  const ref = useRef<HTMLDivElement>(null);
  const pinned = useRef(true);

  useEffect(() => {
    const element = ref.current;
    if (element && pinned.current) {
      element.scrollTop = element.scrollHeight;
    }
  }, [dependency]);

  const onScroll = () => {
    const element = ref.current;
    if (!element) return;
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    pinned.current = distanceFromBottom < 40;
  };

  return { ref, onScroll };
}

export function ConsolePanel({
  logs,
  monitorText,
  monitorActive,
  canMonitor,
  onStartMonitor,
  onStopMonitor,
  onClearLogs,
  onClearMonitor,
}: Props) {
  const [tab, setTab] = useState<Tab>("log");
  const logScroll = useStickyScroll(logs.length);
  const monitorScroll = useStickyScroll(monitorText.length);

  // Switch to the monitor automatically when the board starts talking.
  useEffect(() => {
    if (monitorActive) setTab("monitor");
  }, [monitorActive]);

  const bootFindings = useMemo(() => analyzeBootLog(monitorText), [monitorText]);

  return (
    <Card
      title="Output"
      hint="Flasher log and live serial output from the board."
      flush
      actions={
        <div className="tabs" style={{ marginLeft: "auto" }}>
          <button className={`tab${tab === "log" ? " tab--active" : ""}`} onClick={() => setTab("log")}>
            Log
          </button>
          <button className={`tab${tab === "monitor" ? " tab--active" : ""}`} onClick={() => setTab("monitor")}>
            Serial monitor
          </button>
        </div>
      }
    >
      {tab === "log" ? (
        <>
          <div className="console console--tall" ref={logScroll.ref} onScroll={logScroll.onScroll}>
            {logs.length === 0 ? (
              <div className="console__empty">Nothing yet. Connect a board to begin.</div>
            ) : (
              logs.map((entry) => (
                <div key={entry.id} className={`log-line log-line--${entry.level}`}>
                  <span className="log-line__time">{formatTime(entry.timestamp)}</span>
                  <span className="log-line__text">{entry.message}</span>
                </div>
              ))
            )}
          </div>
          <div className="toolbar">
            <span className="muted">{logs.length} entries</span>
            <div className="toolbar__spacer" />
            <button className="btn btn--ghost btn--sm" onClick={onClearLogs} disabled={logs.length === 0}>
              <TrashIcon size={13} /> Clear
            </button>
          </div>
        </>
      ) : (
        <>
          {bootFindings.length > 0 && (
            <div style={{ padding: "14px 14px 0", display: "flex", flexDirection: "column", gap: 10 }}>
              {bootFindings.map((finding) => (
                <div
                  key={finding.id}
                  className={`notice notice--${finding.severity === "error" ? "error" : "warning"}`}
                >
                  <span className="notice__icon">
                    <AlertIcon size={16} />
                  </span>
                  <div className="notice__body">
                    <div className="notice__title">{finding.title}</div>
                    <p style={{ marginBottom: 8 }}>{finding.explanation}</p>
                    <ol className="fixes">
                      {finding.fixes.map((fix) => (
                        <li key={fix}>
                          <span>{fix}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="console console--tall" ref={monitorScroll.ref} onScroll={monitorScroll.onScroll}>
            {monitorText.length === 0 ? (
              <div className="console__empty">
                {monitorActive
                  ? "Listening at 115200 baud. Press the board's reset button to see it boot."
                  : "The serial monitor is closed."}
              </div>
            ) : (
              monitorText
            )}
          </div>

          <div className="toolbar">
            {monitorActive ? (
              <button className="btn btn--sm btn--danger" onClick={onStopMonitor}>
                Stop monitor
              </button>
            ) : (
              <button className="btn btn--sm" onClick={onStartMonitor} disabled={!canMonitor}>
                <TerminalIcon size={13} /> Open monitor
              </button>
            )}
            <div className="toolbar__spacer" />
            <button className="btn btn--ghost btn--sm" onClick={onClearMonitor} disabled={monitorText.length === 0}>
              <TrashIcon size={13} /> Clear
            </button>
          </div>
        </>
      )}
    </Card>
  );
}
